import { Component, OnInit, Output, EventEmitter, AfterContentInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators, AbstractControl } from '@angular/forms';
import { map } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ITrialService } from '../../services/Trial/trial.service.interface';
import { PaymentService } from '../../services/payment/payment.service';
import { loadStripe, Stripe, StripeCardElement } from '@stripe/stripe-js';

@Component({
  selector: 'app-payment-information',
  templateUrl: './payment-information.component.html',
  styleUrls: ['./payment-information.component.scss']
})
export class PaymentInformationComponent implements OnInit, AfterContentInit, OnDestroy {

  formGroup: FormGroup;
  trialService: ITrialService;
  errorMessage: string;
  isCompleted: boolean;
  catalyst: boolean = true;
  catalystPinnacle: boolean = false;
  subscription: Subscription;
  stripe: Stripe;
  cardElement: StripeCardElement;
  @Output() onCreateForm: EventEmitter<FormGroup>;
  @Output() onStripeCardEventEmit = new EventEmitter<boolean>();
  @Output() onPlanSelected = new EventEmitter<string>();

  constructor(private paymentService: PaymentService, fb: FormBuilder, trialService: ITrialService) {
    this.trialService = trialService;
    this.onCreateForm = new EventEmitter<FormGroup>();
    this.errorMessage = "";
    this.isCompleted = false;
    this.formGroup = fb.group({});
    this.subscription = paymentService.getPaymentMethodRequest$.subscribe(
      generatePaymentMethod => {
        if (generatePaymentMethod) {
          this.generatePaymentMethod(this.cardElement);
        }
    });
  }

  ngOnInit() {
    this.onCreateForm.emit(this.formGroup);
    this.onStripeCardEventEmit.emit(this.isCompleted);
    this.onPlanSelected.emit(this.catalyst ? 'catalyst' : (this.catalystPinnacle ? 'catalystPinnacle' : 'none'));
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.cardElement.destroy();
  }

  ngAfterContentInit() {
    this.LoadStripe();
  }

  LoadStripe()
  {
    this.trialService.getStripePublicKey().subscribe(key => {
      loadStripe(key)
        .then(stripe => {
          this.stripe = stripe;
          this.MountStripeCardElement(stripe);
        })
        .catch(err => console.log(err));
    }, error => {
      this.errorMessage = "An error occurred while loading the payment method.";
      this.isCompleted = false;
    });
  }

  onCatalystChange(value: boolean) {
    this.catalyst = value;
    this.catalystPinnacle = value ? false : this.catalystPinnacle;
    this.onPlanSelected.emit(value ? 'catalyst' : 'none');
  }

  onCatalystPinnacleChange(value: boolean) {
    this.catalystPinnacle = value;
    this.catalyst = value ? false : this.catalyst;
    this.onPlanSelected.emit(value ? 'catalystPinnacle' : 'none');
  }

  MountStripeCardElement(stripe: Stripe)
  {
    const elements = stripe.elements({
      fonts: [
        {
          cssSrc: "https://fonts.googleapis.com/css?family=Roboto"
        }
      ],
      locale: 'auto'
    });

    this.cardElement = elements.create('card', {
      iconStyle: 'solid',
      style: {
        base: {
          color: '#32325D',
          fontWeight: 500,
          fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif',
          fontSize: '16px',
          fontSmoothing: 'antialiased',
  
          ':-webkit-autofill': {
            color: '#fce883',
          },
          '::placeholder': {
            color: '#CFD7DF',
          },
        },
        invalid: {
          color: '#E25950',
        },
      },
    });

    this.cardElement.on('change', ({ empty, error, complete }) => {
      if (complete) {
        this.isCompleted = true;
        this.errorMessage = "";
      } else {
        this.isCompleted = false;
        this.errorMessage = error && error.message ? error.message : (empty ? "Must enter a valid card number" : "Please, complete all fields");
      }
      this.onStripeCardEventEmit.emit(this.isCompleted);
    });

    this.cardElement.on('focus', (event) => this.onStripeCardEventEmit.emit(this.isCompleted));
    this.cardElement.on('blur', (event) => this.onStripeCardEventEmit.emit(this.isCompleted));
    this.cardElement.on('ready', (event) => this.onStripeCardEventEmit.emit(this.isCompleted));
    this.cardElement.mount("#stripe-card-element");
  }

  generatePaymentMethod(cardElement: StripeCardElement) {
    this.stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    })
    .then(result => {
      const { error, paymentMethod } = result;
      if (error) {
        this.isCompleted = false;
        this.errorMessage = error.message;
        this.paymentService.paymentMethodGenerated('');
      } else {
        this.paymentService.paymentMethodGenerated(paymentMethod.id);
      }
    })
    .catch(err => {
      this.isCompleted = false;
      this.errorMessage = "An error occurred while trying to register your payment method. Please try again.";
      this.paymentService.paymentMethodGenerated('');
    });
  }

}
